import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Wallet, TrendingUp, Users, Coins, ArrowUpRight, Copy, PiggyBank } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function StatCard({ icon: Icon, label, value, sub, accent = "emerald", testid, delay }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
      className="card-surface rounded-2xl p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-slate-400 uppercase tracking-wider">{label}</span>
        <Icon className={`h-5 w-5 text-${accent}-400`} />
      </div>
      <div className="text-2xl sm:text-3xl font-extrabold font-mono">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </motion.div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const refLink = `${window.location.origin}/register?ref=${data?.referral_code || user?.referral_code}`;
  const copyRef = () => { navigator.clipboard.writeText(refLink); toast.success("Referral link copied!"); };

  const b = data?.balance || {};

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Welcome, {user?.name}</h1>
          <p className="text-sm text-slate-400 mt-1">Here's your live USDT portfolio</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <StatCard testid="user-balance-display" icon={Wallet} label="Available Balance" value={`$${fmt(b.available)}`} sub="Withdraw-able USDT" delay={0} />
        <StatCard testid="stat-invested" icon={PiggyBank} label="Invested" value={`$${fmt(b.invested)}`} sub={`${data?.active_investments || 0} active plans`} accent="amber" delay={0.05} />
        <StatCard testid="stat-income" icon={TrendingUp} label="Total ROI Income" value={`$${fmt(b.total_income)}`} sub="Daily earnings" delay={0.1} />
        <StatCard testid="stat-referral" icon={Users} label="Referral Income" value={`$${fmt(b.total_referral)}`} sub={`${data?.direct_referrals || 0} direct`} accent="sky" delay={0.15} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 card-surface rounded-2xl p-6">
          <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Deposit", icon: Wallet, to: "/deposit", id: "quick-deposit" },
              { label: "Invest", icon: TrendingUp, to: "/plans", id: "quick-invest" },
              { label: "Withdraw", icon: ArrowUpRight, to: "/withdraw", id: "quick-withdraw" },
              { label: "Team", icon: Users, to: "/team", id: "quick-team" },
            ].map((a) => (
              <button key={a.to} data-testid={a.id} onClick={() => navigate(a.to)}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white/5 hover:bg-emerald-500/10 hover:text-emerald-400 transition-colors text-sm">
                <a.icon className="h-5 w-5" /> {a.label}
              </button>
            ))}
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
            <div className="rounded-xl border border-border p-4">
              <div className="text-slate-400 text-xs">Total Deposited</div>
              <div className="font-mono font-bold text-lg mt-1">${fmt(b.total_deposited)}</div>
            </div>
            <div className="rounded-xl border border-border p-4">
              <div className="text-slate-400 text-xs">Total Withdrawn</div>
              <div className="font-mono font-bold text-lg mt-1">${fmt(b.total_withdrawn)}</div>
            </div>
          </div>
        </div>

        <div className="card-surface rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <Coins className="h-5 w-5 text-emerald-400" />
            <h3 className="text-lg font-semibold">Your Referral</h3>
          </div>
          <div className="text-xs text-slate-400 mb-1">Referral Code</div>
          <div className="font-mono text-emerald-400 text-lg mb-4" data-testid="referral-code-display">{data?.referral_code || user?.referral_code}</div>
          <div className="text-xs text-slate-400 mb-1">Invite Link</div>
          <div className="flex gap-2">
            <div className="flex-1 truncate text-xs bg-white/5 rounded-lg px-3 py-2 font-mono">{refLink}</div>
            <Button data-testid="copy-referral-btn" onClick={copyRef} size="icon" className="bg-emerald-500 hover:bg-emerald-600 shrink-0">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
